import { createSlice } from '@reduxjs/toolkit';

// LocalStorage keys
const STUDENT_KEY = 'student';
const STUDENT_TOKEN_KEY = 'studentToken';
const STUDENT_ENROLLMENT_KEY = 'studentEnrollment';

// Utility functions for localStorage
const saveToLocalStorage = (key, data) => {
  window.localStorage.setItem(key, JSON.stringify(data));
};

const removeFromLocalStorage = (key) => {
  window.localStorage.removeItem(key);
};

const getFromLocalStorage = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || null;
  } catch {
    return null;
  }
};

const initialState = {
  isAuthenticated: !!getFromLocalStorage(STUDENT_TOKEN_KEY),
  student: getFromLocalStorage(STUDENT_KEY),
  enrollment: getFromLocalStorage(STUDENT_ENROLLMENT_KEY),
  token: getFromLocalStorage(STUDENT_TOKEN_KEY),
  loading: false,
  error: null,
};

const studentAuthSlice = createSlice({
  name: 'studentAuth',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Login success
    loginSuccess: (state, action) => {
      const { student, token, enrollment } = action.payload;
      state.isAuthenticated = true;
      state.student = student;
      state.token = token;
      state.enrollment = enrollment;
      state.loading = false;
      state.error = null;

      saveToLocalStorage(STUDENT_KEY, student);
      saveToLocalStorage(STUDENT_TOKEN_KEY, token);
      if (enrollment) {
        saveToLocalStorage(STUDENT_ENROLLMENT_KEY, enrollment);
      }
    },

    // Logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.student = null;
      state.token = null;
      state.enrollment = null;
      state.loading = false;
      state.error = null;

      removeFromLocalStorage(STUDENT_KEY);
      removeFromLocalStorage(STUDENT_TOKEN_KEY);
      removeFromLocalStorage(STUDENT_ENROLLMENT_KEY);
    },

    // Update student profile
    updateStudent: (state, action) => {
      state.student = { ...state.student, ...action.payload };
      saveToLocalStorage(STUDENT_KEY, state.student);
    },

    // Update enrollment
    updateEnrollment: (state, action) => {
      state.enrollment = action.payload;
      saveToLocalStorage(STUDENT_ENROLLMENT_KEY, action.payload);
    },

    // Restore from localStorage
    setStudentFromLocalStorage: (state) => {
      const student = getFromLocalStorage(STUDENT_KEY);
      const token = getFromLocalStorage(STUDENT_TOKEN_KEY);
      const enrollment = getFromLocalStorage(STUDENT_ENROLLMENT_KEY);

      if (student && token) {
        state.isAuthenticated = true;
        state.student = student;
        state.token = token;
        state.enrollment = enrollment;
      } else {
        state.isAuthenticated = false;
        state.student = null;
        state.token = null;
        state.enrollment = null;
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  loginSuccess,
  logout,
  updateStudent,
  updateEnrollment,
  setStudentFromLocalStorage,
  clearError,
} = studentAuthSlice.actions;

// Selectors
export const selectIsStudentAuthenticated = (state) => state.studentAuth.isAuthenticated;
export const selectStudent = (state) => state.studentAuth.student;
export const selectStudentToken = (state) => state.studentAuth.token;
export const selectStudentEnrollment = (state) => state.studentAuth.enrollment;
export const selectStudentLoading = (state) => state.studentAuth.loading;
export const selectStudentError = (state) => state.studentAuth.error;

export default studentAuthSlice.reducer;
